<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@state/stores/auth'
import { usePremiumStore } from '@state/stores/premium'
import DataLayer from '@services/data-layer/index'
import { useAnalytics } from '@composables/use-analytics'
import { timer } from './../../../utils/time'
import { supportEmail } from './../../../configuration'
import paywallTracker from '@services/mixpanel/paywall-tracker'
import { ENTITLEMENT_RESULTS } from '@services/mixpanel/analytics-spec'
import premiumFunnelOrigin from '@utils/premium-funnel-origin'

const DELAYED_TIMEOUT_IN_S = 20
const FAILURE_TIMEOUT_IN_S = 60
const POLLING_DELAY_IN_MS = 2000
const COUNTER_TIMER_INTERVAL_IN_MS = 1000

const route = useRoute()
const authStore = useAuthStore()
const premiumStore = usePremiumStore()
const analyticsService = useAnalytics()

const counter = ref(0)
const isDelayed = ref(false)
const status = ref('WAITING') // WAITING, COMPLETED, ERROR
const isPaymentSuccessful = ref(false)

let counterTimer = null
let entitlementResultTracked = false

const activeSubscription = computed(() => premiumStore.activeSubscription)

const paymentMethod = computed(() => premiumStore.paymentMethod)

const countingDots = computed(() => {
    if (counter.value % 3 === 0) return '.'
    if (counter.value % 3 === 1) return '..'
    return '...'
})

function trackEntitlementResult(result) {
    if (entitlementResultTracked) return
    entitlementResultTracked = true
    paywallTracker.onEntitlementSyncResult(result, route.query.transaction_id || null)
}

async function getSubscriptions() {
    const memberId = authStore.currentUser?.members_id?.id
    const subscriptions = await DataLayer.subscriptions.getSubscriptionsByMemberId(memberId)
    if (subscriptions.body?.some(subscription => subscription.status === 'active')) {
        status.value = 'COMPLETED'
        trackEntitlementResult(ENTITLEMENT_RESULTS.SUCCESS)
    }
}

async function startSubscriptionPolling() {
    if (status.value === 'COMPLETED') return

    await getSubscriptions()
    await timer(POLLING_DELAY_IN_MS)
    startSubscriptionPolling()
}

function startCounting() {
    counter.value = 0
    counterTimer = setInterval(() => {
        if (status.value === 'COMPLETED') {
            clearInterval(counterTimer)
            counterTimer = null
            return
        }
        counter.value++

        if (counter.value >= DELAYED_TIMEOUT_IN_S) {
            isDelayed.value = true
        }
        if (counter.value >= FAILURE_TIMEOUT_IN_S) {
            isDelayed.value = false
            status.value = 'ERROR'
            trackEntitlementResult(ENTITLEMENT_RESULTS.TIMEOUT)
            clearInterval(counterTimer)
            counterTimer = null
        }
    }, COUNTER_TIMER_INTERVAL_IN_MS)
}

function onBeginListening() {
    window.location.href = '/'
}

watch(paymentMethod, () => {
    if (paymentMethod.value && activeSubscription.value) {
        analyticsService.premiumUpsellCompleted({
            isSuccessful: isPaymentSuccessful.value,
            planId: activeSubscription.value?.plan?.id,
            amount: activeSubscription.value?.plan?.price,
            method: paymentMethod.value,
        })
    }
})

onMounted(() => {
    // The funnel ends here; a remembered origin must not leak into a
    // later, unrelated funnel exit.
    premiumFunnelOrigin.clear()
    isPaymentSuccessful.value = route.query.state === 'success'

    if (isPaymentSuccessful.value) {
        // Plan breakdown is restored from localStorage inside the tracker
        // (captured at checkout, before the payment redirect).
        paywallTracker.onPurchaseSucceeded()
        paywallTracker.onEntitlementSyncStarted(
            route.query.product_id || null,
            route.query.transaction_id || null
        )
        startSubscriptionPolling()
    }

    startCounting()
})

onBeforeUnmount(() => {
    if (counterTimer) {
        clearInterval(counterTimer)
        counterTimer = null
    }
})
</script>

<template>
    <div>
        <BaseSection padded>
            <div v-if="status === 'WAITING'">
                <h1>Activating Premium {{ countingDots }}</h1>
                <h6 class="description">
                    {{
                        isDelayed
                            ? 'This is taking a bit longer than expected. Please be patient...'
                            : 'Please wait a few seconds while we upgrade your account...'
                    }}
                </h6>
            </div>
            <div v-else-if="status === 'COMPLETED'">
                <h1 class="main-header">All Set!</h1>
                <h6 class="description">Thanks for upgrading to Premium!</h6>
                <div>
                    <BaseButton
                        button-type="highlight"
                        @click="onBeginListening"
                    >BEGIN LISTENING</BaseButton>
                </div>
            </div>
            <div v-else-if="status === 'ERROR'">
                <h1 class="main-header">Activating Premium</h1>
                <h6 class="description">Looks like something went wrong. Please contact <u><a :href="`mailto:${supportEmail}`">support</a></u>.</h6>
            </div>
        </BaseSection>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.main-header {
    @include h3;

    letter-spacing: -0.5px;
}

.description {
    margin-bottom: 50px;
    font-weight: 300;
    line-height: 1.5;

    @extend %heading;
}

@include desktop {
    .main-header {
        @include h1;

        margin-bottom: $s-xxl;
    }

    .description {
        margin-bottom: 100px;
    }
}
</style>
